import { ReactNode, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useRole } from '../contexts/RoleContext'
import logo from '../LogoBusinessIntelligence.png'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { instance, accounts } = useMsal()
  const location = useLocation()
  const { isAdmin } = useRole()

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  // Build nav items based on user role
  const navItems = useMemo(() => {
    const items = [
      { path: '/dashboard', label: 'Dashboard' },
      { path: '/customers', label: 'Customers' },
      { path: '/usage', label: 'Usage' },
      { path: '/azure-billing', label: 'Azure Billing' },
    ]

    // Add admin-only items
    if (isAdmin) {
      items.push({ path: '/setup', label: 'Setup' })
      items.push({ path: '/audit-logs', label: 'Audit Log' })
    }

    return items
  }, [isAdmin])

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/dashboard" className="flex items-center">
                  <img
                    src={logo}
                    alt="Business Intelligence"
                    className="h-10 w-auto"
                  />
                  <span className="ml-3 text-xl font-bold text-primary-600 hidden lg:block">
                    Business Portal
                  </span>
                </Link>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navItems.map((item) => {
                  const isActive = item.path === '/dashboard'
                    ? location.pathname === '/dashboard'
                    : location.pathname.startsWith(item.path)
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        isActive
                          ? 'border-primary-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )
                })}
              </div>
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-700 mr-4">
                {accounts[0]?.name || 'User'}
              </span>
              <button
                onClick={handleLogout}
                className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  )
}
